import gql from "graphql-tag";

export const getClients = gql`
    query getClients {
        clients: parties(filter: { typeIn: [Client] }) {
            _id
            name
            accountingCurrency
            firstTradeDate
            accounts {
                _id
                name
                description
                parentAccountId
                type
                childrenAccounts {
                    _id
                }
                benchmarks {
                    instrumentId
                    currency
                    instrument {
                        currency
                    }
                    comment
                    showBenchmark
                }
            }
            topAccount {
                benchmarks {
                    instrumentId
                    currency
                    instrument {
                        currency
                    }
                    comment
                    showBenchmark
                }
            }
        }
    }
`;

export const getPerformanceData = gql`
    query getPerformanceData(
        $clientId: GraphQLObjectId!
        $endDate: GraphQLDateString
        $endDateString: GraphQLDateString
        $accountingCurrency: CurrencyEnum = SEK
    ) {
        client: party(_id: $clientId) {
            _id
            name
            instruments {
                _id
            }
            accounts {
                _id
                name
                description
                parentAccountId
                type
                childrenAccounts {
                    _id
                }
                benchmarks {
                    instrumentId
                    currency
                    mainBenchmark
                    showBenchmark
                    showExternal
                    offset
                    comment
                    instrument {
                        name
                        longName
                        timeSeries(typeIn: ["Price(Close)"], exchanged: $accountingCurrency, endDate: $endDateString) {
                            type
                            dates
                            values
                        }
                    }
                }
                modelIndexBenchmark {
                    name
                    currency
                    timeSeries {
                        items {
                            date
                            value
                        }
                    }
                }
                weights {
                    date
                    model
                }
            }
            topAccount {
                _id
            }
        }

        riskFreeTimeSeries: timeseries(
            includeItems: true
            filter: { typeIn: "Price(Close)", instrumentNameIn: "OMRXMM", endDate: $endDate }
        ) {
            dates
            values
            type
        }

        tradingmanager(
            groupPositionsBy: AccountId
            filterZeroPositions: true
            filter: { clientIds: [$clientId], endDate: $endDateString }
            lookThrough: true
        ) {
            instrumentId
            accountId
            valuationDate
            quantity
            name
        }

        performance(clientId: $clientId, filter: { endDate: $endDateString }, lookThrough: true, useCache: true) {
            id
            currency
            dates
            values
            cashFlows
            series
            instrumentPerformances {
                accountId
                instrumentId
                firstTradeDate
                instrument {
                    name
                    longName
                    bloombergTicker
                    currency
                    modelType
                    creditRating
                    issuer {
                        industryCode
                    }
                    benchmark {
                        _id
                        currency
                        name
                        longName
                        timeSeries(typeIn: ["Price(Close)"], exchanged: $accountingCurrency, endDate: $endDateString) {
                            type
                            dates
                            values
                        }
                    }
                }
                series
                values
                cashFlows
                positions
            }
        }
    }
`;

export const getInstrumentCloseSeries = gql`
    query getInstrumentCloseSeries($instrumentIds: [GraphQLObjectId!]!, $endDate: GraphQLDateString) {
        timeseries(includeItems: true, filter: { instrumentIdIn: $instrumentIds, typeIn: ["Price(Close)"], endDate: $endDate }) {
            instrumentId
            dates
            values
            type
            instrument {
                name
                longName
                modelType
            }
        }
    }
`;

/** Daily instrument valuations (not Price(Close) timeseries). Same source the /performance engine uses. */
export const getInstrumentValuations = gql`
    query getInstrumentValuations($instrumentIds: [GraphQLObjectId!]!, $startDate: GraphQLSwedenDate, $endDate: GraphQLSwedenDate) {
        valuations(instrumentIds: $instrumentIds, startDate: $startDate, endDate: $endDate) {
            instrumentId
            date
            records {
                prices {
                    value
                }
            }
        }
    }
`;

/**
 * Total returns for the instruments shown in the pivot table.
 *
 * Deliberately goes through the top-level `timeseries` query rather than `instrument.timeSeries` on
 * each instrumentPerformance: the latter resolves per instrument through a dataloader that batches
 * every (instrumentId, type) pair into one `$or`, which times out in mongo once a portfolio has a few
 * hundred instruments, and it loads full item history because the date range is not pushed down.
 * `timeseries` issues a single `objectId: { $in }` find with `date` bounds applied in the query.
 */
export const getPivotInstrumentReturns = gql`
    query getPivotInstrumentReturns(
        $instrumentIds: [GraphQLObjectId!]!
        $fxNames: [String!]!
        $startDate: GraphQLDateString
        $fxStartDate: GraphQLDateString
        $endDate: GraphQLDateString
    ) {
        prices: timeseries(
            includeItems: true
            filter: {
                instrumentIdIn: $instrumentIds
                typeIn: ["Return(Total)"]
                startDate: $startDate
                endDate: $endDate
            }
        ) {
            instrumentId
            type
            dates
            values
        }
        # A Return(Total) value is the return since the previous observation, so the period a compounded
        # return covers starts at the last observation on or before the period start. Its own value is not
        # used, only its date, and sparse series can have it long before the fetched window opens.
        anchor: timeseries(
            includeItems: true
            lastItem: true
            filter: { instrumentIdIn: $instrumentIds, typeIn: ["Return(Total)"], endDate: $startDate }
        ) {
            instrumentId
            dates
        }
        # Deliberately without a date filter: the last observation each series has at all, so a blank
        # performance cell can say whether the instrument has no series or one that stops before the period.
        latest: timeseries(includeItems: true, lastItem: true, filter: { instrumentIdIn: $instrumentIds, typeIn: ["Return(Total)"] }) {
            instrumentId
            dates
        }
        fx: timeseries(
            includeItems: true
            filter: { instrumentNameIn: $fxNames, typeIn: ["Price(Close)"], startDate: $fxStartDate, endDate: $endDate }
        ) {
            dates
            values
            instrument {
                name
            }
        }
    }
`;

export const getFundPerformance = gql`
    query getFundPerformance($endDate: GraphQLDateString, $includeDetails: Boolean! = false) {
        fundPerformance(endDate: $endDate) {
            dates
            values
            cashFlows
            id @include(if: $includeDetails)
            currency @include(if: $includeDetails)
            endValue @include(if: $includeDetails)
            series @include(if: $includeDetails)
            performanceItems @include(if: $includeDetails) {
                date
                value
                cashFlow
                totalReturn
                series
            }
            fundPerformances {
                fundId
                fund {
                    name
                    _id @include(if: $includeDetails)
                }
                dates
                values
                cashFlows
                currency @include(if: $includeDetails)
                endValue @include(if: $includeDetails)
                series @include(if: $includeDetails)
            }
            instrumentPerformances @include(if: $includeDetails) {
                instrumentId
                accountId
                firstTradeDate
                instrument {
                    name
                }
                dates
                values
                cashFlows
                series
                positions
            }
        }
    }
`;

export const getPieData = gql`
    query getPieData($clientId: GraphQLObjectId!, $endDate: GraphQLDateString) {
        tradingmanager(
            groupPositionsBy: AccountId
            filterZeroPositions: true
            filter: { clientIds: [$clientId], endDate: $endDate }
            lookThrough: false
        ) {
            instrumentId
            accountId
            account {
                type
                description
            }
            name
            currency
            quantity
            valuationDate
            valuationPrice
            localExposure
            exposure
            fxRate
            portfolioInstrumentId
            valuationDate
        }
    }
`;
